import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import {
  LayoutDashboard,
  Users as UsersIcon,
  ScrollText,
  SlidersHorizontal,
  ShieldAlert,
} from "lucide-react";
import { Container } from "@/components/layout/Container";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { AdminDashboardPage } from "@/pages/admin/AdminDashboardPage";
import { AdminUsersPage } from "@/pages/admin/AdminUsersPage";
import { AdminUserDetailPage } from "@/pages/admin/AdminUserDetailPage";
import { AdminAuditLogPage } from "@/pages/admin/AdminAuditLogPage";
import { AdminConfigPage } from "@/pages/admin/AdminConfigPage";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/users", label: "Users", icon: UsersIcon, end: false },
  { to: "/admin/audit", label: "Audit log", icon: ScrollText, end: false },
  { to: "/admin/config", label: "Config", icon: SlidersHorizontal, end: false },
];

function NotAuthorized() {
  return (
    <Container size="md" className="py-16">
      <Card className="flex flex-col items-center p-10 text-center">
        <div className="grid h-12 w-12 place-items-center rounded-full bg-danger-soft text-danger">
          <ShieldAlert className="h-6 w-6" />
        </div>
        <h1 className="mt-4 font-display text-2xl font-semibold">Not authorized</h1>
        <p className="mt-2 max-w-sm text-text-muted">
          The admin console is restricted to platform administrators. If you
          believe this is a mistake, contact the StaxED team.
        </p>
        <Button asChild variant="secondary" className="mt-6">
          <NavLink to="/dashboard">Back to dashboard</NavLink>
        </Button>
      </Card>
    </Container>
  );
}

/**
 * Admin section shell. Mounted at /admin/* and renders its OWN nested
 * <Routes> so the parent router only wires /admin/* -> <AdminLayout/>.
 *
 * Gating: the whole section requires the signed-in user to be SUPER_ADMIN.
 * While the current-user query is in flight we show a light skeleton (the
 * route already sits behind RequireAuth, so we know there's a session);
 * non-admins get a clean "Not authorized" panel. The backend additionally
 * re-checks the role on every endpoint, so this is defence in depth.
 */
export function AdminLayout() {
  const me = useCurrentUser();

  if (me.isLoading) {
    return (
      <Container size="lg" className="py-10">
        <Skeleton className="h-9 w-48" />
        <Skeleton className="mt-6 h-64 w-full" />
      </Container>
    );
  }

  if (me.data?.role_id !== "SUPER_ADMIN") {
    return <NotAuthorized />;
  }

  return (
    <Container size="lg" className="py-6 lg:py-8">
      <div className="mb-6 flex flex-col gap-1">
        <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
          Admin console
        </h1>
        <p className="text-sm text-text-muted">
          Platform operations -- users, audit trail and runtime config.
        </p>
      </div>

      {/* Section nav. Horizontal scroll on narrow screens keeps every tab
          reachable without wrapping awkwardly. */}
      <nav className="mb-6 flex gap-1 overflow-x-auto border-b border-border pb-px">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cn(
                "relative flex items-center gap-2 whitespace-nowrap rounded-t-lg px-3 py-2 text-sm font-medium",
                "transition-colors duration-150 -mb-px border-b-2",
                isActive
                  ? "border-brand text-brand"
                  : "border-transparent text-text-muted hover:text-text",
              )
            }
          >
            <Icon className="h-4 w-4 flex-shrink-0" />
            {label}
          </NavLink>
        ))}
      </nav>

      <Routes>
        <Route index element={<AdminDashboardPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="users/:userId" element={<AdminUserDetailPage />} />
        <Route path="audit" element={<AdminAuditLogPage />} />
        <Route path="config" element={<AdminConfigPage />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </Container>
  );
}
